import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, Row, Col, Alert, Spin, Modal, Badge, Button, Statistic, Space, Tag, Table, Typography, Select, Input, Segmented, message } from 'antd';
import { useSelector } from 'react-redux';
import {
  fetchDashboardFilterOptions,
  fetchDashboardAnalytics,
  fetchAllSessions
} from '../../../../Service/Dashboard_API/dashboardApi';
import {
  VideoCameraOutlined,
  BookOutlined,
  UserOutlined,
  TeamOutlined,
  BankOutlined,
  DesktopOutlined,
  ReadOutlined,
  CalendarOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  SyncOutlined,
  SearchOutlined
} from '@ant-design/icons';
import {
  ClusterInstitutionAnalyticsCard
} from './AnalyticsCharts';
import ClusterLiveAttendanceReportCard from './ClusterLiveAttendanceReportCard';
import RealTimeStats from '../RealTimeStats';

const { Text, Title } = Typography;
const { Option } = Select;

const formatDateTime = (dateStr) => {
  if (!dateStr) return 'N/A';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}-${month}-${year} ${hours}:${minutes}`;
};

const AdminDashboard = ({ onMenuSelect }) => {
  const token = useSelector((state) => state.auth?.token);

  // Global Filters State
  const [filters, setFilters] = useState({
    academic_year_id: 'all',
    institution_id: 'all',
    instructor_id: 'all',
    menu_key: 'all'
  });
  const [filterOptions, setFilterOptions] = useState({});

  // Table Card Level Filter State
  const [tableStatusFilter, setTableStatusFilter] = useState('all');
  const [tableTypeFilter, setTableTypeFilter] = useState('all');
  const [tableSearchText, setTableSearchText] = useState('');

  // Analytics Data State
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Live sessions modal state
  const [liveSessions, setLiveSessions] = useState([]);
  const [showLiveModal, setShowLiveModal] = useState(false);

  const [isFilterLoaded, setIsFilterLoaded] = useState(false);

  // Fetch filter options on mount & default to current active Academic Year
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const res = await fetchDashboardFilterOptions();
        if (res.success) {
          setFilterOptions(res);
          if (res.academicYears && res.academicYears.length > 0) {
            const activeAy = res.academicYears.find(
              y => y.status?.toLowerCase() === 'active' || y.status === 'Active' || y.status === 'ACTIVE' || y.status === 1 || y.status === '1'
            ) || res.academicYears.find(y => {
              if (!y.start_date || !y.end_date) return false;
              const today = new Date();
              const start = new Date(y.start_date);
              const end = new Date(y.end_date);
              return !isNaN(start.getTime()) && !isNaN(end.getTime()) && today >= start && today <= end;
            }) || res.academicYears[0];

            if (activeAy) {
              const defaultAyId = String(activeAy.academic_year_id);
              setFilters((prev) => ({ ...prev, academic_year_id: defaultAyId }));
            }
          }
        }
      } catch (err) {
        console.error('Failed to load filter options:', err);
      } finally {
        setIsFilterLoaded(true);
      }
    };
    loadFilterOptions();
  }, []);

  // Fetch Analytics data on filter change & real-time background polling
  const loadAnalytics = useCallback(async (isBackground = false) => {
    try {
      if (!isBackground) {
        setLoading(true);
        setError(null);
      }
      const res = await fetchDashboardAnalytics(filters);
      if (res && res.success) {
        setAnalytics(res);
      } else if (!isBackground) {
        setError(res?.message || 'Failed to load analytics');
      }
    } catch (err) {
      console.error('Analytics load error:', err);
      if (!isBackground) {
        setError(err.message || 'Error connecting to analytics endpoint');
      }
    } finally {
      if (!isBackground) {
        setLoading(false);
      }
    }
  }, [filters]);

  useEffect(() => {
    if (!isFilterLoaded) return;
    loadAnalytics();
    // Real-time auto sync polling every 15 seconds without reloading
    const analyticsInterval = setInterval(() => {
      loadAnalytics(true);
    }, 15000);
    return () => clearInterval(analyticsInterval);
  }, [isFilterLoaded, loadAnalytics]);

  // Live session polling
  useEffect(() => {
    const fetchLive = async () => {
      try {
        const data = await fetchAllSessions();
        const sessionList = Array.isArray(data)
          ? data
          : (data && data.success && Array.isArray(data.sessions) ? data.sessions : (data && Array.isArray(data.sessions) ? data.sessions : []));
        setLiveSessions(sessionList);
      } catch (err) {
        console.error('Failed to fetch live sessions:', err);
      }
    };
    fetchLive();
    const interval = setInterval(fetchLive, 10000);
    return () => clearInterval(interval);
  }, []);

  
  
  // Filtered Table Data for Table Card
  const filteredTableSessions = useMemo(() => {
    if (!analytics?.recentSessions) return [];
    return analytics.recentSessions.filter((row) => {
      const matchStatus = tableStatusFilter === 'all' || row.status === tableStatusFilter;
      const matchType = tableTypeFilter === 'all' || row.type === tableTypeFilter;
      const queryStr = tableSearchText.toLowerCase();
      const matchSearch =
        !tableSearchText ||
        (row.subject && row.subject.toLowerCase().includes(queryStr)) ||
        (row.instructor && row.instructor.toLowerCase().includes(queryStr)) ||
        (row.topic && row.topic.toLowerCase().includes(queryStr));

      return matchStatus && matchType && matchSearch;
    });
  }, [analytics, tableStatusFilter, tableTypeFilter, tableSearchText]);

  // Export CSV Report
  
  const summary = analytics?.summary || {};
  const liveCount = liveSessions.filter((s) => s.status === 'live').length;
  const scheduledCount = liveSessions.filter((s) => s.status === 'scheduled').length;

  const cardItems = [
    {
      title: 'Live Studio Rooms',
      value: liveCount,
      active: liveCount,
      icon: <VideoCameraOutlined style={{ fontSize: 24, color: liveCount > 0 ? '#ef4444' : '#60a5fa' }} />,
      bgColor: liveCount > 0 
        ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.12) 0%, rgba(220, 38, 38, 0.04) 100%)'
        : 'linear-gradient(135deg, rgba(96, 165, 250, 0.12) 0%, rgba(59, 130, 246, 0.04) 100%)',
      borderColor: liveCount > 0 ? 'rgba(239, 68, 68, 0.2)' : 'rgba(96, 165, 250, 0.2)',
      tag: liveCount > 0 ? `🔴 Active (${liveCount} live)` : scheduledCount > 0 ? `Offline (${scheduledCount} scheduled)` : 'No Live Sessions',
      tagColor: liveCount > 0 ? 'red' : scheduledCount > 0 ? 'gold' : 'default',
      targetMenu: 'live-sessions'
    },
    {
      title: 'Active Courses',
      value: summary.totalCourses || 0,
      icon: <BookOutlined style={{ fontSize: 24, color: '#10b981' }} />,
      bgColor: 'linear-gradient(135deg, rgba(16, 185, 129, 0.12) 0%, rgba(4, 120, 87, 0.04) 100%)',
      borderColor: 'rgba(16, 185, 129, 0.2)',
      tag: 'Curriculum',
      tagColor: 'green',
      targetMenu: 'courses'
    },
    {
      title: 'Study Modules',
      value: summary.totalModules || 0,
      icon: <DesktopOutlined style={{ fontSize: 24, color: '#f59e0b' }} />,
      bgColor: 'linear-gradient(135deg, rgba(245, 158, 11, 0.12) 0%, rgba(217, 119, 6, 0.04) 100%)',
      borderColor: 'rgba(245, 158, 11, 0.2)',
      tag: 'Syllabus modules',
      tagColor: 'orange',
      targetMenu: 'courses'
    },
    {
      title: 'Video',
      value: summary.totalVideoIndex || 0,
      icon: <ReadOutlined style={{ fontSize: 24, color: '#8b5cf6' }} />,
      bgColor: 'linear-gradient(135deg, rgba(139, 92, 246, 0.12) 0%, rgba(109, 40, 217, 0.04) 100%)',
      borderColor: 'rgba(139, 92, 246, 0.2)',
      tag: 'Indexed Videos',
      tagColor: 'purple',
      targetMenu: 'module-videos'
    },
    {
      title: 'Daily Sessions',
      value: summary.totalDailySessions || 0,
      icon: <CalendarOutlined style={{ fontSize: 24, color: '#f59e0b' }} />,
      bgColor: 'linear-gradient(135deg, rgba(245, 158, 11, 0.12) 0%, rgba(217, 119, 6, 0.04) 100%)',
      borderColor: 'rgba(245, 158, 11, 0.2)',
      tag: `${summary.totalDailyUnits || 0} Units Delivered`,
      tagColor: 'orange',
      targetMenu: 'session-mgmt'
    },
    {
      title: 'Institutions',
      value: summary.totalInstitutions || 0,
      icon: <BankOutlined style={{ fontSize: 24, color: '#10b981' }} />,
      bgColor: 'linear-gradient(135deg, rgba(16, 185, 129, 0.12) 0%, rgba(4, 120, 87, 0.04) 100%)',
      borderColor: 'rgba(16, 185, 129, 0.2)',
      tag: 'Active Institutions',
      tagColor: 'green',
      targetMenu: 'institutions'
    },
    {
      title: 'Instructors & Faculty',
      value: summary.totalInstructors || 0,
      icon: <TeamOutlined style={{ fontSize: 24, color: '#06b6d4' }} />,
      bgColor: 'linear-gradient(135deg, rgba(6, 182, 212, 0.12) 0%, rgba(14, 116, 144, 0.04) 100%)',
      borderColor: 'rgba(6, 182, 212, 0.2)',
      tag: 'Teaching Staff',
      tagColor: 'cyan',
      targetMenu: 'instructors'
    },
    {
      title: 'Candidates & Marks',
      value: summary.totalCandidates || 0,
      icon: <UserOutlined style={{ fontSize: 24, color: '#ec4899' }} />,
      bgColor: 'linear-gradient(135deg, rgba(236, 72, 153, 0.12) 0%, rgba(190, 24, 93, 0.04) 100%)',
      borderColor: 'rgba(236, 72, 153, 0.2)',
      tag: `Avg Score: ${summary.avgCandidateScore || 0}%`,
      tagColor: 'pink',
      targetMenu: 'candidates-mgmt'
    }
  ];

  
  const filteredCardItems = cardItems;

  return (
    <div style={{ padding: '4px 0' }}>
      {error && <Alert message="Analytics Warning" description={error} type="warning" showIcon style={{ marginBottom: 20 }} />}

      {loading && !analytics ? (
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <Spin size="large" tip="Generating multi-dimensional analytics..." />
        </div>
      ) : (
        <>
          {/* Summary Metric Cards wrapped in a parent Card */}
          <Card
            title={
              <Text strong style={{ fontSize: 16, color: 'var(--text-color, #374151)' }}>
                Executive Metrics Overview
              </Text>
            }
            extra={
              <Space size="small">
                <Text strong style={{ fontSize: 13 }}>Academic Year:</Text>
                <Select showSearch optionFilterProp="children"
                  size="small"
                  value={filters.academic_year_id}
                  onChange={(val) => setFilters(prev => ({ ...prev, academic_year_id: val }))}
                  style={{ width: 140 }}
                  popupMatchSelectWidth={false}
                >
                  <Option value="all">All Academic Years</Option>
                  {(filterOptions?.academicYears || []).map((ay) => (
                    <Option key={ay.academic_year_id} value={String(ay.academic_year_id)}>
                      {ay.academic_year}
                    </Option>
                  ))}
                </Select>
              </Space>
            }
            style={{
              borderRadius: 16,
              marginBottom: 24,
              background: 'var(--panel-color, #ffffff)',
              borderColor: 'var(--border-color, rgba(0,0,0,0.08))'
            }}
          >
            <Row gutter={[16, 16]}>
              {filteredCardItems.map((item, idx) => (
                <Col xs={24} sm={12} md={8} lg={6} key={idx} style={{ flexGrow: 1 }}>
                  <Card
                    hoverable
                    onClick={() => onMenuSelect && onMenuSelect(item.targetMenu)}
                    style={{
                      borderRadius: 12,
                      background: item.bgColor,
                      borderColor: item.borderColor,
                      boxShadow: '0 4px 15px rgba(0,0,0,0.03)'
                    }}
                    bodyStyle={{ padding: '16px 18px' }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <Text type="secondary" style={{ fontSize: 12, fontWeight: 600 }}>
                          {item.title}
                        </Text>
                        <Title level={3} style={{ margin: '4px 0', fontWeight: 800, color: 'var(--text-color, #1f2937)' }}>
                          {item.value}
                        </Title>
                      </div>
                      <div
                        style={{
                          padding: 10,
                          borderRadius: 12,
                          backgroundColor: 'var(--panel-color, rgba(255, 255, 255, 0.85))',
                          border: '1px solid var(--border-color)',
                          backdropFilter: 'blur(4px)'
                        }}
                      >
                        {item.icon}
                      </div>
                    </div>
                    <div style={{ marginTop: 8 }}>
                      <Tag color={item.tagColor} style={{ borderRadius: 10, fontWeight: 600 }}>
                        {item.tag}
                      </Tag>
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          </Card>




          {/* Cluster-Wise Live Attendance Summary Report Card */}
          <div style={{ marginBottom: 24 }}>
            <ClusterLiveAttendanceReportCard
              academicYears={filterOptions?.academicYears}
              clusters={filterOptions?.clusters}
              defaultAcademicYearId={filters.academic_year_id !== 'all' ? filters.academic_year_id : null}
            />
          </div>

          {/* Candidates Performance Analytics (Pie + Table) */}
          <div style={{ marginBottom: 24 }}>
            <ClusterInstitutionAnalyticsCard
              InstitutionsData={analytics?.clusterInstitutionStats}
              academicYears={filterOptions?.academicYears}
              clusters={filterOptions?.clusters}
            />
          </div>



          {/* Server Health Status at Bottom */}
          <div style={{ marginTop: 24 }}>
            <RealTimeStats />
          </div>
        </>
      )}

      {/* Live Sessions Modal */}
      <Modal
        title="Active Live Sessions"
        open={showLiveModal}
        onCancel={() => setShowLiveModal(false)}
        footer={null}
        width={700}
      >
        <Table
          dataSource={liveSessions.filter((s) => s.status === 'live')}
          rowKey="rtmp_session_id"
          pagination={{ pageSize: 20 }}
          scroll={{ x: 'max-content' }}
          columns={[
            { title: 'Title', dataIndex: 'title' },
            { title: 'Instructor', dataIndex: 'instructor_name' },
            {
              title: 'Action',
              key: 'action',
              render: (_, record) => (
                <Button type="primary" size="small" onClick={() => onMenuSelect && onMenuSelect('live-sessions')}>
                  Watch Live
                </Button>
              )
            }
          ]}
        />
      </Modal>
    </div>
  );
};

export default AdminDashboard;